import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { allowedFileTypes } from '../../../../../backend/src/matter/doc/allowed-extensions';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';

export function Guide() {
  const { t } = useTranslation();
  const [fileTypesModalOpen, setFileTypesModalOpen] = useState(false);

  return (
    <>
      <Breadcrumb items={[{ href: '/guide', label: 'User Guide', active: true }]} />

      <PageContainer>
        <div className="mx-auto mb-16 w-full max-w-3xl">
          <div className="my-12">
            <h1 className="font-serif text-4xl font-medium">User Guide</h1>
            <p className="my-3 text-sm text-gray-600">Learn how to use Wexler effectively.</p>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="flex flex-col gap-8 text-gray-700">
              {/* Overview */}
              <section id="overview" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-gray-200 pb-2 text-2xl font-semibold text-gray-900">Overview</h2>
                <div className="space-y-4">
                  <p className="text-base font-medium">
                    Wexler helps litigators in private practice and in house find key facts and insights in large
                    volumes of documents.
                  </p>

                  <div className="my-4 border-t border-gray-200" />

                  <p>The product is designed as a workflow tool in which you can:</p>

                  <ul className="ml-6 list-disc space-y-2">
                    <li>
                      Extract and review facts (guided by our intelligent AI which helps to identify the most important
                      facts).
                    </li>
                    <li>Identify key persons and entities in your documents.</li>
                    <li>Build chronologies from your facts.</li>
                    <li>Review inconsistencies which our AI has identified between different pieces of evidence.</li>
                    <li>See high level summaries of your documents and their key issues.</li>
                    <li>Ask follow up questions about the matter through our conversational interface, Kim.</li>
                    <li>Process realtime transcriptions with live fact extraction.</li>
                  </ul>

                  <p className="text-sm text-gray-600">
                    We recommend that Wexler be used for 'hot' or 'key' documents rather than triaging every document in
                    the matter, due to the costs involved.
                  </p>

                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-sm font-medium">
                      We work closely with our users to develop the product based on their needs. We can respond quickly
                      to bugs or issues, should they arise and we are always on hand to help. If you have feedback or
                      queries, please contact us directly at{' '}
                      <a className="font-semibold text-blue-600 hover:text-blue-700" href="mailto:support@wexler.ai">
                        support@wexler.ai
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </section>

              {/* Getting Started */}
              <section id="getting-started" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-dashed border-gray-300 pb-2 text-xl font-semibold text-gray-700">
                  Getting Started
                </h2>
                <p>
                  To get started you first need to create a <strong>Matter</strong>.
                </p>
              </section>

              {/* Matters */}
              <section id="matters" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-gray-200 pb-2 text-2xl font-semibold text-gray-900">Matters</h2>
                <div className="space-y-4">
                  <p>
                    <strong>Matters</strong> are where you you store all of your documents. When you upload documents to
                    a Matter, Wexler will extract key information (facts, entities, inconsistencies) from these. You can
                    then view all of this extracted information, or create a <strong>Subset</strong> to just view data
                    from a subset of your documents.
                  </p>

                  <p>
                    Matters are also where you manage access to information within the Matter. Any users who are added
                    to a Matter will be able to see all of the information within it.
                  </p>

                  <div className="rounded-md bg-blue-50 p-4">
                    <h4 className="mb-2 font-semibold text-gray-900">Creating a Matter</h4>
                    <p className="text-sm text-gray-700">
                      Click "Create a New Matter" to start. You will be guided through uploading documents, providing
                      basic information, and setting up your Matter context (issues, parties, and additional context).
                    </p>
                  </div>
                </div>
              </section>

              {/* Matter Context */}
              <section id="matter-context" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-gray-200 pb-2 text-2xl font-semibold text-gray-900">
                  Matter Context
                </h2>
                <div className="space-y-4">
                  <p>
                    When creating a Matter, you provide <strong>context</strong> that helps Wexler's AI understand what
                    is important. Matter context is made up of three parts: <strong>Issues</strong>,{' '}
                    <strong>Parties</strong>, and <strong>Additional Context</strong>.
                  </p>

                  <p>
                    You can either enter this information manually, or upload documents (such as instructions or
                    pleadings) and have Wexler's AI automatically extract issues and parties from them.
                  </p>

                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <h4 className="mb-2 font-semibold text-gray-900">Extracting Context from Documents</h4>
                    <p className="text-sm text-gray-700">
                      Upload PDF documents (e.g. letters of instruction, particulars of claim, or other key pleadings)
                      and Wexler will automatically extract issues, parties, and any other key context from them. You
                      can then review and edit the extracted information before finalising.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <div className="space-y-3">
                  <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">Issues</h3>
                  <p>
                    <strong>Issues</strong> are the key issues in your Matter. Wexler's AI uses these to determine which
                    extracted facts should be considered most relevant. Each fact will be tagged with a Relevance
                    (Relevant, Maybe Relevant or Not Relevant) and a Significance (explanation of why the Relevance was
                    chosen). Facts will also be tagged with the issues they pertain to.
                  </p>

                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                    <p className="font-semibold text-gray-700">
                      <strong>Note:</strong> Issues don't impact which facts are extracted, but which facts are{' '}
                      <em>relevant</em>. You can come back and change the issues once the upload has been processed.
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                      Wexler has a strict definition of relevance which is about the material importance of the fact to
                      the matter. Background information is generally not relevant.
                    </p>
                  </div>

                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <h4 className="mb-2 font-semibold text-gray-800">Example: Infected Blood Inquiry</h4>
                    <ol className="list-decimal space-y-2 pl-6 text-sm text-gray-700">
                      <li>
                        <strong>Blood supply system structure</strong> - The structure of the blood supply system,
                        including the roles and responsibilities of government bodies, healthcare {t('organisation')}s,
                        and medical practitioners in decision-making and policy implementation.
                      </li>
                      <li>
                        <strong>Coordination and standards</strong> - The effectiveness of coordination between these
                        entities and the influence of international standards and practices on their decisions.
                      </li>
                      <li>
                        <strong>Infection risk knowledge</strong> - The development and flow of knowledge about
                        infection risks across {t('organisation')}s, including how and when key information was
                        communicated to professionals and patients.
                      </li>
                    </ol>
                  </div>
                </div>
              </section>

              <section>
                <div className="space-y-3">
                  <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">Parties</h3>
                  <p>
                    <strong>Parties</strong> are the key individuals and {t('organisation')}s involved in the Matter.
                    Each party has a <strong>name</strong>, a <strong>role</strong> (Claimant, Defendant, or Other), and
                    a <strong>type</strong> (Person or {capitalizeFirstLetter(t('organisation'))}). You can also
                    designate which party is your client.
                  </p>
                  <p>Like issues, parties can be extracted automatically from uploaded documents or added manually.</p>
                </div>
              </section>

              <section>
                <div className="space-y-3">
                  <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">
                    Additional Context
                  </h3>
                  <p>
                    <strong>Additional Context</strong> is a text field where you can enter any additional information
                    about the Matter that doesn't fit neatly into issues or parties. This might include details about
                    the jurisdiction, key dates, procedural history, relief sought, or any other background that helps
                    Wexler's AI better understand the Matter.
                  </p>
                  <p>
                    When you extract context from documents, Wexler may suggest additional context based on what it
                    finds. You can accept, edit, or discard these suggestions.
                  </p>
                </div>
              </section>

              <section>
                <div className="space-y-3">
                  <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">Document Upload</h3>
                  <p>When creating a Matter, or after the Matter has been created, you can upload your documents.</p>

                  <p>
                    Our file upload supports many formats including EML, MHT, DocX, Doc, PPTx, RTF, PDF, MSG,
                    Spreadsheets (Excel, CSV, etc.) and many more, see a full list{' '}
                    <button
                      onClick={() => setFileTypesModalOpen(true)}
                      className="font-semibold text-blue-600 underline hover:text-blue-700"
                    >
                      here
                    </button>
                    .
                  </p>

                  <p className="text-xs text-gray-500">
                    Contact the Wexler team to inform us if your file type is not supported.
                  </p>

                  <p>
                    We also have integrations with Box, OneDrive, Dropbox and SharePoint (you can also drag and drop
                    files from iManage Drive).
                  </p>

                  <div className="mt-4 flex gap-3 rounded-lg border border-blue-200 bg-blue-50/80 p-4 text-sm text-blue-700">
                    <div>
                      <p className="mb-1 text-sm font-semibold text-blue-800">Uploading a large number of files?</p>
                      <p className="text-xs text-blue-700">
                        Uploading lots of (&gt;1000) files directly may cause performance issues or browser crashes.
                        Compress files into a ZIP archive first, and upload that for better reliability.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Subsets */}
              <section id="subsets" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-gray-200 pb-2 text-2xl font-semibold text-gray-900">Subsets</h2>
                <div className="space-y-4">
                  <p>
                    Within a matter you can create <strong>Subsets</strong>. These are collections of documents through
                    which you can view just the facts/entities/inconsistencies which come from those documents.
                  </p>

                  <p>
                    Subsets allow you to organise your documents and focus on specific aspects of your matter. Each
                    subset can have its own issues, and so when viewing data through a Subset facts will be shown with
                    the Relevance and Significance which has been determined based on the issues of that Subset.
                  </p>
                </div>
              </section>

              {/* Facts */}
              <section id="facts" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-gray-200 pb-2 text-2xl font-semibold text-gray-900">Facts</h2>
                <div className="space-y-4">
                  <p>
                    Wexler will extract all facts from your documents, these can be viewed on the <strong>Facts</strong>{' '}
                    page. You can view the facts from all documents, or, view facts through a subset to just see facts
                    from that subset's documents.
                  </p>

                  <p>
                    Wexler's algorithm extracts all facts included within the submitted documents. The product is
                    deliberately conservative, and extracts every fact, so that users can filter down by different
                    keywords for different elements of a given subset.
                  </p>

                  <p>
                    Key facts are <span className="font-semibold text-yellow-600">highlighted</span> and you can use
                    filters to show only key facts.
                  </p>

                  <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">
                    Fact Information
                  </h3>
                  <p>
                    Each fact contains several pieces of information, including description, date, source document(s),
                    document type, source text etc. This information has been extracted from the documents and you can
                    edit the fact description and fact date.
                  </p>

                  <p>
                    The <strong>relevant</strong> and <strong>significance</strong> fields are created by Wexler's AI.
                    For relevance, Wexler's AI determines if a fact is highly relevant, not relevant, or maybe relevant
                    (these require additional review from the user). The significance field describes why a fact is
                    considered relevant/not relevant to the issues you inputted when creating the Matter or Subset.
                  </p>

                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h4 className="mb-2 font-semibold text-gray-900">Using the Facts Page</h4>
                    <p className="text-sm text-gray-700">
                      Use filters to narrow down which facts you want to view. You can search keywords in facts as well
                      as apply filters based on things like, document, dates, relevance etc.. Make use of our{' '}
                      <strong>Natural Language Filtering</strong>, which allows you to apply discrete filters by typing
                      them out in natural language e.g. "Find all relevant facts between 1990 and 1994 about the
                      structure of the blood supply system".
                    </p>
                    <p className="mt-2 text-sm text-gray-700">
                      Once you have filtered to the facts you want, you can select these and create a{' '}
                      <strong>chronology</strong> from these facts, or add them to an existing chronology.
                    </p>
                  </div>
                </div>
              </section>

              {/* Documents */}
              <section id="documents" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-gray-200 pb-2 text-2xl font-semibold text-gray-900">Documents</h2>
                <div className="space-y-4">
                  <p>
                    The documents page allows you to see and all of the documents in your subset, read the metadata
                    about these documents, and view the document text itself.
                  </p>

                  <p>
                    If you want to add more documents, you can do this by adding them at the Matter level and uploading
                    additional documents, then adding them to your subset.
                  </p>

                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <Icon className="lucide--alert-triangle mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <p className="text-sm text-amber-800">
                      Warning: updating your subset with new issues may require reprocessing and be time consuming.
                    </p>
                  </div>
                </div>
              </section>

              {/* Chronologies */}
              <section id="chronologies" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-gray-200 pb-2 text-2xl font-semibold text-gray-900">
                  Chronologies
                </h2>
                <div className="space-y-4">
                  <p>
                    The Chronologies page allows you to view all of your chronologies. Click on a chronology to view it.
                  </p>

                  <p>
                    Facts within a chronology can be edited (this will not update the fact outside of the chronology)
                    and facts can be manually added/removed. You can also verify facts within a chronology to mark that
                    you have reviewed and approved it.
                  </p>

                  <p>Once you are happy with your chronology you can export it and share it with your team.</p>
                </div>
              </section>

              {/* Inconsistencies */}
              <section id="inconsistencies" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-gray-200 pb-2 text-2xl font-semibold text-gray-900">
                  Inconsistencies
                </h2>
                <div className="space-y-4">
                  <p>Our AI identifies and extracts inconsistencies across the uploaded documents.</p>

                  <p>
                    These inconsistencies are labelled as <strong>Critical</strong>, <strong>Severe</strong> or{' '}
                    <strong>Minor</strong> based on their severity and how relevant they are to the key issues. We also
                    identify if this inconsistency has been found within the same piece of testimony, across different
                    testimony, across multiple pieces of evidence, or when comparing testimony and evidence.
                  </p>

                  <p>
                    Additionally you can use our side by side document viewer to see exactly where the inconsistent
                    pieces of evidence exist in the documents.
                  </p>
                </div>
              </section>

              {/* Entities */}
              <section id="entities" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-gray-200 pb-2 text-2xl font-semibold text-gray-900">Entities</h2>
                <div className="space-y-4">
                  <p>
                    Wexler automatically identifies and extracts <strong>Entities</strong> from your documents. Entities
                    are key persons, {t('organisation')}s, and places that appear throughout your matter. These entities
                    are linked to the facts where they are mentioned, making it easy to track who did what and where
                    events occurred.
                  </p>

                  <p>
                    You can view all entities on the <strong>Entities</strong> page, which shows key entities on the
                    subset overview and provides a dedicated page for browsing all entities.
                  </p>

                  <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">Entity Types</h3>
                  <p>Entities are categorized into three types:</p>
                  <ul className="ml-6 list-disc space-y-2">
                    <li>
                      <strong>Person</strong> - Individual people mentioned in your documents
                    </li>
                    <li>
                      <strong>{capitalizeFirstLetter(t('organisation'))}</strong> - Companies, institutions, and other{' '}
                      {t('organisation')}s
                    </li>
                    <li>
                      <strong>Place</strong> - Locations, addresses, and geographical references
                    </li>
                  </ul>

                  <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">
                    Entity Information
                  </h3>
                  <p>Each entity contains several pieces of information:</p>
                  <ul className="ml-6 list-disc space-y-2">
                    <li>
                      <strong>Name</strong> - The primary name of the entity as it appears in documents
                    </li>
                    <li>
                      <strong>Type</strong> - Whether it's a Person, {t('organisation')}, or Place
                    </li>
                    <li>
                      <strong>Aliases</strong> - Alternative names or variations used to refer to the same entity
                    </li>
                    <li>
                      <strong>Description</strong> - A summary of the entity's role and significance in the matter
                    </li>
                  </ul>

                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h4 className="mb-2 font-semibold text-gray-900">Using Entities</h4>
                    <p className="text-sm text-gray-700">
                      Click on any entity to view related facts. You can also filter the Facts page by entity to see all
                      facts that mention a specific person, {t('organisation')}, or place.
                    </p>
                    <p className="mt-2 text-sm text-gray-700">
                      Use the search and filter options on the Entities page to find specific entities by name, type, or
                      other criteria.
                    </p>
                  </div>

                  <p>
                    Entities can be exported to Excel or Word format, allowing you to share entity lists with your team
                    or include them in reports.
                  </p>
                </div>
              </section>

              {/* Kim */}
              <section id="kim" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-gray-200 pb-2 text-2xl font-semibold text-gray-900">Kim</h2>
                <div className="space-y-4">
                  <p>
                    Kim is Wexler's conversational interface, which allows you to ask open ended questions about your
                    whole subset.
                  </p>

                  <p>
                    Ask questions in natural language and Kim will search through your documents and facts to provide
                    relevant answers with citations.
                  </p>

                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <h4 className="mb-2 font-semibold text-gray-900">Tips for Using Kim</h4>
                    <ul className="list-disc space-y-1 pl-6 text-sm text-gray-700">
                      <li>Be specific with your questions</li>
                      <li>Reference entities, dates, or events for focused answers</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Realtime */}
              <section id="realtime" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-gray-200 pb-2 text-2xl font-semibold text-gray-900">
                  Realtime Transcription
                </h2>
                <div className="space-y-4">
                  <p>
                    The Realtime feature allows you to record audio in realtime and have Wexler identify inconsistencies
                    between the transcript and facts in your Matter. This is for realtime fact checking for depositions
                    or interviews.
                  </p>

                  <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                    <h4 className="mb-2 font-semibold text-gray-900">Realtime Features</h4>
                    <ul className="list-disc space-y-1 pl-6 text-sm text-gray-700">
                      <li>Live transcription with speaker diarization</li>
                      <li>Automatic fact extraction from transcripts</li>
                      <li>Inconsistency detection across testimony</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Admins */}
              <section id="admins" className="scroll-mt-20">
                <h2 className="mb-4 border-b-2 border-gray-200 pb-2 text-2xl font-semibold text-gray-900">Admins</h2>
                <div className="space-y-4">
                  <p>Admins have permissions to manage an {t('organisation')} and its users.</p>
                  {/* Relativity Integrations */}
                  <div className="space-y-3">
                    <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">
                      Relativity Integrations
                    </h3>
                    <p>
                      Wexler supports integrations with Relativity. To configure Relativity integrations, visit the{' '}
                      <Link
                        to="/organisation-settings/integrations"
                        className="font-semibold text-blue-600 underline hover:text-blue-700"
                      >
                        Integrations
                      </Link>{' '}
                      section in {capitalizeFirstLetter(t('organisation'))} Settings.
                    </p>
                  </div>

                  {/* Roles */}
                  <div className="space-y-3">
                    <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">Roles</h3>
                    <p>
                      Wexler uses a role-based access control system to manage permissions within your{' '}
                      {t('organisation')}.
                    </p>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <h4 className="mb-2 font-semibold text-gray-900">Default Roles</h4>
                      <p className="mb-3 text-sm text-gray-700">
                        Wexler comes with three default roles that are available in every {t('organisation')}:
                      </p>
                      <ul className="ml-6 list-disc space-y-2 text-sm text-gray-700">
                        <li>
                          <strong>Administrator</strong> - Full access to all features including managing users,
                          Matters, credit limits, and integrations.
                        </li>
                        <li>
                          <strong>Analytics</strong> - Access to usage dashboards, credit limit management, and user
                          management.
                        </li>
                        <li>
                          <strong>Member</strong> - Basic access.
                        </li>
                      </ul>
                    </div>

                    <p>
                      In addition to the default roles, you can create custom roles with specific permission sets
                      tailored to your {t('organisation')}'s needs. Custom roles allow you to grant users exactly the
                      permissions they need for their work.
                    </p>

                    <p>
                      To manage roles and permissions, visit the{' '}
                      <Link
                        to="/organisation-settings/roles"
                        className="font-semibold text-blue-600 underline hover:text-blue-700"
                      >
                        Roles & Permissions
                      </Link>{' '}
                      section in {capitalizeFirstLetter(t('organisation'))} Settings.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </PageContainer>

      <Dialog open={fileTypesModalOpen} onOpenChange={setFileTypesModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="lucide--file-check h-5 w-5 text-gray-700" />
              Permitted file types
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <p className="text-gray-700">Wexler supports the following file types:</p>
            <div className="flex flex-wrap gap-x-4 gap-y-2 rounded-md border bg-gray-50 p-3">
              {allowedFileTypes.map((fileType: string) => (
                <div
                  className="rounded-md border bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700"
                  key={fileType}
                >
                  {fileType}
                </div>
              ))}
            </div>
            <ul className="flex list-inside list-disc flex-col gap-2 py-2 text-sm text-gray-700">
              <li>The maximum file size supported is 3GB, or 120GB for ZIP archives</li>
              <li>The maximum number of files supported is 200,000</li>
            </ul>
            <p className="text-xs text-gray-600">
              Reach out to the Wexler team if these limits are not sufficient for your case.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
